import React, { useState, useEffect } from "react";
import {
    Box, Typography, Card, CardContent, Stack, CircularProgress,
    Chip, Tabs, Tab, Grid, Avatar, Button, Fade,
    Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton,
    ListItemText, DialogActions, Snackbar, Alert, IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import InventoryIcon from "@mui/icons-material/Inventory";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { obtenerListasDelUsuario } from "../api/listas";
import { agregarArticulosBatch } from "../api/articulos";

export default function Novedades() {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // Estado para el diálogo de añadir a lista
    const [listas, setListas] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [loadingListas, setLoadingListas] = useState(false);

    // Estado para el Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchAlerts = async () => {
        try {
            const { data } = await api.get("/stats/alerts");
            setAlerts(data);
        } catch (err) {
            console.error("Error fetching alerts:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
    }, []);

    // Fix scroll: no salta arriba al cambiar de tab
    const handleTabChange = (e, newValue) => {
        const scrollY = window.scrollY;
        setTabValue(newValue);
        requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "instant" }));
    };

    const handleOpenDialog = async (alert) => {
        setSelectedAlert(alert);
        setDialogOpen(true);
        if (listas.length === 0) {
            setLoadingListas(true);
            try {
                const data = await obtenerListasDelUsuario();
                setListas(data);
            } catch (err) {
                console.error("Error fetching lists:", err);
                setSnackbar({ open: true, message: "Error al cargar tus listas", severity: "error" });
            } finally {
                setLoadingListas(false);
            }
        }
    };

    const handleAddToList = async (idLista) => {
        if (!selectedAlert) return;

        try {
            const items = [{
                nombre: selectedAlert.nombre,
                cantidad: 1,
                unidad: "ud",
                precio_unitario: selectedAlert.precio,
                supermercado: selectedAlert.tienda,
                origen: "novedades"
            }];

            await agregarArticulosBatch(idLista, items);
            setSnackbar({ open: true, message: `"${selectedAlert.nombre}" añadido a la lista`, severity: "success" });
            setDialogOpen(false);
        } catch (err) {
            console.error("Error adding to list:", err);
            setSnackbar({ open: true, message: "Error al añadir el producto", severity: "error" });
        }
    };

    const filteredAlerts = alerts.filter(alert => {
        if (tabValue === 0) return true;
        if (tabValue === 1) return alert.tipo === "bajada";
        if (tabValue === 2) return alert.tipo === "nuevo";
        return true;
    });

    // Destacadas: solo si tienen precio_anterior real (no inventado)
    const featuredAlerts = alerts
        .filter(a => a.tipo === "bajada" && a.precio_anterior && a.precio_anterior > a.precio)
        .slice(0, 3);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56, boxShadow: 3 }}>
                    <NotificationsIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "text.primary" }}>
                        Novedades y Alertas
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.7 }}>
                        Descubre las mejores ofertas y productos recién llegados.
                    </Typography>
                </Box>
            </Stack>

            <Grid container spacing={3}>
                {/* Catálogos Digitales */}
                <Grid item xs={12}>
                    <Card sx={{
                        borderRadius: 4,
                        background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                        color: "white",
                        boxShadow: "0 8px 32px rgba(25, 118, 210, 0.2)"
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                <StorefrontIcon /> Catálogos Digitales
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                                Acceso directo a los folletos vigentes de tus tiendas de confianza.
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="contained"
                                    href="https://www.hiperdino.es/folletos/"
                                    target="_blank"
                                    sx={{
                                        bgcolor: "rgba(255,255,255,0.2)",
                                        backdropFilter: "blur(10px)",
                                        "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                                        borderRadius: 2,
                                        px: 3
                                    }}
                                >
                                    HiperDino
                                </Button>
                                <Button
                                    variant="contained"
                                    href="https://tienda.mercadona.es/"
                                    target="_blank"
                                    sx={{
                                        bgcolor: "rgba(255,255,255,0.2)",
                                        backdropFilter: "blur(10px)",
                                        "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                                        borderRadius: 2,
                                        px: 3
                                    }}
                                >
                                    Mercadona
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Bajadas de precio — solo si hay precio_anterior real en BD */}
                {featuredAlerts.length > 0 && (
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <LocalFireDepartmentIcon sx={{ color: "#ff9800" }} /> Bajadas de precio
                        </Typography>
                        <Grid container spacing={2}>
                            {featuredAlerts.map((alert, idx) => (
                                <Grid item xs={12} sm={4} key={`feat-${idx}`}>
                                    <Card sx={{
                                        borderRadius: 4,
                                        border: "2px solid",
                                        borderColor: "primary.light",
                                        height: "100%"
                                    }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                {alert.categoria}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1 }}>
                                                {alert.nombre}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                En {alert.tienda}
                                            </Typography>
                                            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 2 }}>
                                                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 900 }}>
                                                    {alert.precio.toFixed(2)}€
                                                </Typography>
                                                <Typography variant="body2" sx={{ textDecoration: "line-through", opacity: 0.5 }}>
                                                    {alert.precio_anterior.toFixed(2)}€
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                )}

                {/* Listado de Alertas con Filtros */}
                <Grid item xs={12}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="alert filters">
                            <Tab label="Todas" sx={{ fontWeight: 700 }} />
                            <Tab label="Bajadas" icon={<TrendingDownIcon fontSize="small" />} iconPosition="start" sx={{ fontWeight: 700 }} />
                            <Tab label="Novedades" icon={<NewReleasesIcon fontSize="small" />} iconPosition="start" sx={{ fontWeight: 700 }} />
                        </Tabs>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={60} thickness={4} />
                        </Box>
                    ) : filteredAlerts.length === 0 ? (
                        <Fade in timeout={500}>
                            <Card sx={{
                                borderRadius: 4,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                py: 8,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
                            }}>
                                <CardContent sx={{ textAlign: "center" }}>
                                    <Box sx={{
                                        display: "inline-flex",
                                        p: 2,
                                        borderRadius: "50%",
                                        bgcolor: "rgba(25, 118, 210, 0.08)",
                                        color: "primary.main",
                                        mb: 3
                                    }}>
                                        {tabValue === 0 ? (
                                            <InventoryIcon sx={{ fontSize: 48 }} />
                                        ) : (
                                            <NotificationsOffIcon sx={{ fontSize: 48 }} />
                                        )}
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: "text.primary" }}>
                                        {tabValue === 0
                                            ? "¡Todo despejado por aquí!"
                                            : "No hay alertas de este tipo"}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: "text.secondary", mb: 4, maxWidth: 450, mx: "auto" }}>
                                        {tabValue === 0
                                            ? "Actualmente no hay alertas disponibles. Añade productos a tus listas para que Compi pueda vigilar los precios por ti."
                                            : `Parece que hoy no hay ${tabValue === 1 ? "bajadas de precio" : "productos nuevos"}. ¡Echa un vistazo al resto de alertas!`}
                                    </Typography>
                                    <Stack direction="row" spacing={2} justifyContent="center">
                                        {tabValue !== 0 && (
                                            <Button
                                                variant="contained"
                                                onClick={() => setTabValue(0)}
                                                sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
                                            >
                                                Ver todas las alertas
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate("/listas")}
                                            sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
                                        >
                                            Ir a mis listas
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Fade>
                    ) : (
                        <Stack spacing={2}>
                            {filteredAlerts.map((alert, idx) => (
                                <Fade in key={idx} timeout={300 + idx * 50}>
                                    <Card sx={{
                                        borderRadius: 3,
                                        transition: "transform 0.2s",
                                        "&:hover": { transform: "scale(1.01)", boxShadow: 4 },
                                        border: "1px solid rgba(0,0,0,0.05)"
                                    }}>
                                        <CardContent sx={{ py: "16px !important" }}>
                                            <Grid container alignItems="center" spacing={2}>
                                                <Grid item>
                                                    <Avatar sx={{
                                                        bgcolor: alert.tipo === "bajada" ? "success.light" : "info.light",
                                                        color: alert.tipo === "bajada" ? "success.dark" : "info.dark"
                                                    }}>
                                                        {alert.tipo === "bajada" ? <TrendingDownIcon /> : <NewReleasesIcon />}
                                                    </Avatar>
                                                </Grid>
                                                <Grid item xs>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                            {alert.nombre}
                                                        </Typography>
                                                        <Chip label={alert.categoria} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }} />
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Disponible en <strong>{alert.tienda}</strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item sx={{ textAlign: "right" }}>
                                                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 900 }}>
                                                        {alert.precio.toFixed(2)}€
                                                    </Typography>
                                                    {/* Precio tachado solo si hay dato real */}
                                                    {alert.precio_anterior && alert.precio_anterior > alert.precio && (
                                                        <Typography variant="body2" sx={{ textDecoration: "line-through", opacity: 0.5 }}>
                                                            {alert.precio_anterior.toFixed(2)}€
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<AddShoppingCartIcon />}
                                                        sx={{ borderRadius: 2 }}
                                                        onClick={() => handleOpenDialog(alert)}
                                                    >
                                                        Añadir
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            ))}
                        </Stack>
                    )}
                </Grid>


            </Grid>

            {/* Diálogo para seleccionar lista */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Añadir a una lista
                    <IconButton onClick={() => setDialogOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {loadingListas ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : listas.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: "center" }}>
                            <Typography variant="body1" sx={{ mb: 2 }}>No tienes listas creadas.</Typography>
                            <Button variant="contained" onClick={() => navigate("/listas")}>Crear mi primera lista</Button>
                        </Box>
                    ) : (
                        <List sx={{ py: 0 }}>
                            {listas.map((lista) => (
                                <ListItem key={lista.idlista} disablePadding divider>
                                    <ListItemButton onClick={() => handleAddToList(lista.idlista)}>
                                        <ListItemText
                                            primary={lista.nombre}
                                            secondary={lista.sala_name}
                                            primaryTypographyProps={{ fontWeight: 700 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%", fontWeight: 700, borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}